import {
  DatabaseIcon,
  MicroscopeIcon,
  CpuIcon,
  UsersIcon,
  FilePlusIcon,
} from './why-reanalyze-icons';
import type { ReanalysisCategoryData } from '@/services';

/* ─── Rich Text Tags ─── */

export const RICH_TEXT_TAGS = { br: () => <br /> } as const;
export const RICH_TEXT_TAGS_SPACE = { br: () => ' ' } as const;

/* ─── Types ─── */

const CATEGORY_KEYS = [
  'variantDb',
  'newDisease',
  'technology',
  'familyStudy',
  'phenotype',
] as const;

export type CategoryKey = (typeof CATEGORY_KEYS)[number];

export interface CategoryConfig {
  key: CategoryKey;
  value: number;
  color: string;
  bgColor: string;
  iconColor: string;
  icon: React.FC<{ className?: string }>;
  badgeKey?: 'categories.variantDb.badge';
}

/* ─── Constants ─── */

export const PIE_CELL_STYLE = {
  transition: 'opacity 0.3s ease',
  outline: 'none',
  cursor: 'pointer',
} as const;

const CATEGORY_CONFIG: CategoryConfig[] = [
  {
    key: 'variantDb',
    value: 0,
    color: '#3B82F6',
    bgColor: 'bg-[#eff6ff]',
    iconColor: 'text-[#3B82F6]',
    icon: DatabaseIcon,
    badgeKey: 'categories.variantDb.badge',
  },
  {
    key: 'newDisease',
    value: 0,
    color: '#A855F7',
    bgColor: 'bg-[#faf5ff]',
    iconColor: 'text-[#A855F7]',
    icon: MicroscopeIcon,
  },
  {
    key: 'phenotype',
    value: 0,
    color: '#10B981',
    bgColor: 'bg-[#ecfdf5]',
    iconColor: 'text-[#10B981]',
    icon: FilePlusIcon,
  },
  {
    key: 'technology',
    value: 0,
    color: '#06B6D4',
    bgColor: 'bg-[#ecfeff]',
    iconColor: 'text-[#06B6D4]',
    icon: CpuIcon,
  },
  {
    key: 'familyStudy',
    value: 0,
    color: '#f54900',
    bgColor: 'bg-[#fff7ed]',
    iconColor: 'text-[#f54900]',
    icon: UsersIcon,
  },
];

/**
 * WordPress 데이터와 정적 카테고리 설정을 병합합니다.
 */
export function buildCategories(
  dynamicData: ReanalysisCategoryData[],
): CategoryConfig[] {
  const valueMap = new Map(dynamicData.map(d => [d.key, d.value]));
  return CATEGORY_CONFIG.map(config => ({
    ...config,
    value: valueMap.get(config.key) ?? config.value,
  }));
}

export function sortCategories(categories: CategoryConfig[]): CategoryConfig[] {
  return [...categories].sort((a, b) => b.value - a.value);
}

export function buildCategoryMap(
  categories: CategoryConfig[],
): Map<CategoryKey, CategoryConfig> {
  return new Map(categories.map(cat => [cat.key, cat]));
}
